import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import fr from "./locales/fr.json";
import en from "./locales/en.json";

export const SUPPORTED_LANGS = ["fr", "en"] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

export const LANG_STORAGE_KEY = "thiia_lang";

/**
 * Public marketing/legal surfaces. Product decision: these pages are
 * French-only, so we force `fr` regardless of `navigator.language`.
 *
 * Matching mirrors `RouteRobotsController`: strict equality OR
 * `pathname.startsWith(prefix + "/")`. A naive `startsWith(prefix)` would
 * make the private `/contacts` route match `/contact`.
 */
const PUBLIC_PREFIXES = [
  "/landing",
  "/use-cases",
  "/contact",
  "/legal",
  "/privacy",
  "/terms",
  "/docs",
  "/security",
  "/comparaisons",
];

export function isPublicPath(pathname: string): boolean {
  if (pathname === "/") return true; // root: exact match only
  return PUBLIC_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + "/"),
  );
}

/**
 * Persist an explicit language choice. i18next's own caching is disabled
 * (`detection.caches: []`) so a forced `fr` on public pages never leaks into
 * the authenticated app — explicit user choices must be written here.
 */
export function persistLanguage(lng: string) {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lng);
  } catch {
    /* ignore */
  }
}

const languageDetector = new LanguageDetector();
languageDetector.addDetector({
  name: "publicFr",
  lookup() {
    // SSR : pas de window. Le rendu serveur doit être déterministe (fr),
    // sinon `navigator` du runtime serveur (en-US) provoque un mismatch
    // d'hydratation avec le client.
    if (typeof window === "undefined") return "fr";
    return isPublicPath(window.location.pathname) ? "fr" : undefined;
  },

  cacheUserLanguage() {
    /* never persist the forced language */
  },
});

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      fr: { translation: fr },
      en: { translation: en },
    },
    fallbackLng: "fr",
    supportedLngs: SUPPORTED_LANGS as unknown as string[],
    nonExplicitSupportedLngs: true, // map en-US -> en
    load: "languageOnly",
    detection: {
      order: ["localStorage", "publicFr", "navigator"],
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: [],
    },
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

export default i18n;
